import type { Review } from '@/scopes/products/user-reviews/types'
import { useIntl } from '@/intl'
import { useAnalytics } from '@/scopes/common/infrastructure/analytics/hooks'
import { horizontalGap, ReadMore, SpaceBetween, tk, Typography } from '@sunwebgroup/ui-components'
import { useState } from 'react'
import { styled } from 'styled-components'
import { createUserReviewsEvent } from '../../../../../user-reviews/infrastructure/analytics'
import { getPartyCompositionType } from '../utils'
import ReviewFlagIcon from './ReviewFlagIcon'
import { ReviewScore } from './ReviewScore'
import { TranslatedByAtribution } from './TranslatedByAttribution'
import { TranslatedText } from './TranslatedText'

export const Card = styled.div`
  padding: ${tk('spacing_lg')};
  border-radius: ${tk('radius_large')};
  box-shadow: ${tk('shadow_md_bottom')};
  background-color: ${tk('color_semantic_bg')};

  &:not(:last-child) {
    margin-bottom: ${tk('spacing_md')};
  }
`

const ShowTranslatedTextButton = styled.button`
  ${Typography} {
    text-decoration: underline;
  }
`

const ShowOriginalText = styled.div`
  ${Typography} {
    color: ${tk('color_semantic_text_subdued')};
    margin-right: ${tk('spacing_2xs')};
  }
`

const TranslationButton = styled.div`
  margin-top: ${tk('spacing_xs')};
`

const StyledSpaceBetween = styled(SpaceBetween)`
  margin-bottom: ${tk('spacing_md')};
  align-items: center;
`

const ReadMoreWrapper = styled.div`
  margin-bottom: ${tk('spacing_md')};
`

const Footer = styled.div`
  ${horizontalGap('spacing_xs')}
`

const StrongText = styled(Typography)`
  color: ${tk('color_semantic_text_strong')};
`

export function UserReview({
  date,
  rating,
  scoreLabel,
  description,
  reviewLanguageCode,
  userName,
  partyCompositionGroupId,
  id,
  departureDate,
}: Review) {
  const intl = useIntl()
  const [showTranslatedText, setShowTranslatedText] = useState(false)
  const { sendEvent } = useAnalytics()

  const handleToggleTranslation = () => {
    sendEvent(
      createUserReviewsEvent({
        description: 'Show review translations',
        element: 'Clickable text',
        result: showTranslatedText ? 'original' : 'translated',
        ecommerce: {
          reviewTransalted: {
            id,
            overallRating: Number.parseFloat(rating),
            country: reviewLanguageCode,
            partyComposition: intl.formatMessage({
              id: `reviews_party_composition_${getPartyCompositionType(partyCompositionGroupId)}_label`,
            }),
          },
        },
      }),
    )
    setShowTranslatedText(!showTranslatedText)
  }

  // If the date is empty (shown as 1970-01-01) we want to show the departure date instead of the review date
  const resolveDate = () => {
    const invalidDate = new Date('1970-01-01')

    if (Number(date) === Number(invalidDate))
      return departureDate

    return intl.formatRelativeDate(date)
  }

  return (
    <Card>
      <StyledSpaceBetween direction="x" justifyContent="space-between">
        <ReviewScore rating={rating} scoreLabel={scoreLabel} />
        <StrongText $variant="body-sm">{resolveDate()}</StrongText>
      </StyledSpaceBetween>
      <ReadMoreWrapper>
        {showTranslatedText && <TranslatedText>{description}</TranslatedText>}
        {!showTranslatedText && (
          <ReadMore charactersShown={255} readMoreText={intl.formatMessage({ id: 'reviews_read_more_label' })}>
            {description || description !== '' ? description : intl.formatMessage({ id: 'reviews_no_text' })}
          </ReadMore>
        )}
      </ReadMoreWrapper>
      <Footer>
        <ReviewFlagIcon languageCode={reviewLanguageCode} />
        <Typography $variant="body-sm-bold">
          {userName || intl.formatMessage({ id: 'reviews_anonymous_user' })}
        </Typography>
        <StrongText $variant="body-sm">
          {intl.formatMessage({
            id: `reviews_party_composition_${getPartyCompositionType(partyCompositionGroupId)}_label`,
          })}
        </StrongText>
      </Footer>
      <TranslationButton>
        {reviewLanguageCode !== 'NL' && showTranslatedText && (
          <SpaceBetween direction="x" justifyContent="space-between">
            <ShowOriginalText>
              <Typography $variant="body-xs-bold">
                {intl.formatMessage(
                  { id: 'reviews_translated_from' },
                  { language: intl.formatMessage({ id: `reviews_language_${reviewLanguageCode.toLowerCase()}` }) },
                )}
              </Typography>
              <ShowTranslatedTextButton onClick={handleToggleTranslation}>
                <Typography $variant="body-xs-bold">
                  {intl.formatMessage({ id: 'reviews_translation_original' })}
                </Typography>
              </ShowTranslatedTextButton>
            </ShowOriginalText>
            <TranslatedByAtribution />
          </SpaceBetween>
        )}
        {reviewLanguageCode !== 'NL' && !showTranslatedText && (
          <ShowTranslatedTextButton onClick={handleToggleTranslation}>
            <Typography $variant="body-xs-bold">
              {intl.formatMessage({ id: 'reviews_translation_translate' })}
            </Typography>
          </ShowTranslatedTextButton>
        )}
      </TranslationButton>
    </Card>
  )
}
